import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { submitCohortQuiz, clearQuizResults } from "../../../features/cohort/cohortSlice";
import QuizQuestion from "../../Lesson/QuizQuestion";
import QuizResults from "../../Lesson/QuizResults";

function QuizWorkspace({ challenge, quizData, progress }) {
  const dispatch = useDispatch();
  const { quizResults, isSubmitting } = useSelector((state) => state.cohort);
  const [answers, setAnswers] = useState({});

  // Initialize answers from previous progress or reset when challenge changes
  useEffect(() => {
    if (progress?.quizAnswers) {
      setAnswers(progress.quizAnswers);
    } else {
      setAnswers({});
    }
    // Clear previous results when challenge changes
    dispatch(clearQuizResults());
  }, [challenge.id, dispatch]);

  const handleAnswerChange = (questionId, selectedAnswers) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: selectedAnswers,
    }));
  };

  const handleSubmit = () => {
    dispatch(
      submitCohortQuiz({
        challengeId: challenge.id,
        answers,
      })
    );
  };

  const handleRetry = () => {
    setAnswers({});
    dispatch(clearQuizResults());
  };

  if (!quizData) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-zinc-400 font-display">Loading quiz...</div>
      </div>
    );
  }

  const { quiz, questions } = quizData;
  const isCompleted = progress?.status === "Completed";
  const answeredCount = Object.keys(answers).filter(
    (qId) => answers[qId] && answers[qId].length > 0
  ).length;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          {/* Quiz Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <h2 className="font-display text-2xl font-semibold text-zinc-200 text-center">
                <span className="text-purple-400">Quiz:</span> {quiz.name}
              </h2>
              {isCompleted && (
                <span className="text-sm px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  Completed
                </span>
              )}
            </div>
            {quiz.description && (
              <p className="text-zinc-400 text-center">{quiz.description}</p>
            )}
          </div>

          {/* Quiz Content */}
          {quizResults ? (
            <QuizResults results={quizResults} onRetry={handleRetry} />
          ) : (
            <>
              <div className="space-y-2">
                {questions.map((question, index) => (
                  <QuizQuestion
                    key={question._id}
                    question={question}
                    questionNumber={index + 1}
                    selectedAnswers={answers[question._id] || []}
                    onAnswerChange={(selected) =>
                      handleAnswerChange(question._id, selected)
                    }
                    disabled={isCompleted}
                  />
                ))}
              </div>

              {/* Submit Section */}
              <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center justify-between">
                <span className="text-sm text-zinc-400 font-display">
                  {answeredCount} of {questions.length} questions answered
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting || isCompleted || answeredCount < questions.length
                  }
                  className={`font-display font-medium px-6 py-3 rounded-lg transition-colors ${
                    isSubmitting || isCompleted || answeredCount < questions.length
                      ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-500 text-white"
                  }`}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : isCompleted
                    ? "Quiz Completed"
                    : "Submit Quiz"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuizWorkspace;
